import Joi from 'joi';

import schemas from 'components/forms/fields/definedSchemas';

const fields = {
    applicant_name_first: {
        ...schemas.textRequired,
        section: 'Name & ID',
        label: 'Legal Name: First',
        autocomplete: 'given-name',
    },
    applicant_name_middle: {
        ...schemas.textOptional,
        section: 'Name & ID',
        label: 'Legal Name: Middle',
        autocomplete: 'additional-name',
    },
    applicant_name_last: {
        ...schemas.textRequired,
        section: 'Name & ID',
        label: 'Legal Name: Last',
        autocomplete: 'family-name',
    },
    applicant_suffix: {
        ...schemas.textOptional,
        section: 'Name & ID',
        label: 'Suffix (Jr., Sr., III)',
    },
    applicant_alias_or_maiden_name: {
        ...schemas.textOptional,
        section: 'Name & ID',
        label: 'Aliases or Maiden Names',
    },
    applicant_us_citizen: {
        ...schemas.boolRequired,
        section: 'Citizenship',
        label: 'Are you a US citizen?',
    },
    applicant_citizen_country: {
        ...schemas.country,
        section: 'Citizenship',
        label: 'Which country are you a citizen of?',
        validation: Joi.when('applicant_us_citizen', {
            is: true,
            then: schemas.country.validation.allow('', null),
            otherwise: schemas.country.validation,
        }),
        autocomplete: 'country',
    },
    applicant_legal_alien: {
        ...schemas.boolRequired,
        section: 'Citizenship',
        label: 'Legal Alien',
        validation: Joi.when('applicant_us_citizen', {
            is: false,
            then: schemas.boolRequired.validation,
            otherwise: schemas.boolRequired.validation.allow('', null),
        }),
    },
    applicant_legal_alien_number: {
        ...schemas.legal_alien_number,
        section: 'Citizenship',
        label: 'Alien Registration Number',
        validation: Joi.when('applicant_legal_alien', {
            is: true,
            then: schemas.legal_alien_number.validation,
            otherwise: schemas.legal_alien_number.validation.allow('', null),
        }),
    },
    applicant_date_of_birth: {
        ...schemas.dateRequiredMaxToday,
        section: 'Birth',
        label: 'Date of Birth',
        validation: schemas.dateRequired.validation
            .max(new Date().setFullYear(new Date().getFullYear() - 16))
            .min(new Date().setFullYear(new Date().getFullYear() - 130))
            .messages({
                'date.max': 'You must be at least 16 years old to file with the NMC',
                'date.min': 'Must be born within the past century',
            }),
    },
    applicant_city_of_birth: {
        ...schemas.textRequired,
        section: 'Birth',
        label: 'City of Birth',
        autocomplete: 'address-level2',
    },
    applicant_state_of_birth: {
        ...schemas.states,
        section: 'Birth',
        label: 'State of Birth',
        autocomplete: 'address-level1',
    },
    applicant_country_of_birth: {
        ...schemas.country,
        section: 'Birth',
        label: 'Country of Birth',
        autocomplete: 'country',
    },
    applicant_eye_color: {
        ...schemas.textRequired,
        section: 'Hair & Eye Color',
        label: 'Color of Eyes',
    },
    applicant_hair_color: {
        ...schemas.textRequired,
        section: 'Hair & Eye Color',
        label: 'Color of Hair',
    },
    applicant_home_address_street: {
        ...schemas.textRequired,
        section: 'Home Address',
        label: 'Home Address',
        autocomplete: 'address-line1',
    },
    applicant_home_address_city: {
        ...schemas.textRequired,
        section: 'Home Address',
        label: 'Home Address City',
        autocomplete: 'address-level2',
    },
    applicant_home_address_state: {
        ...schemas.states,
        section: 'Home Address',
        label: 'Home Address State',
        autocomplete: 'address-level1',
    },
    applicant_home_address_zip: {
        ...schemas.textRequired,
        section: 'Home Address',
        label: 'Home Address Zip',
        autocomplete: 'postal-code',
    },
    applicant_delivery_same_as_home: {
        ...schemas.boolRequired,
        section: 'Delivery Address',
        label: 'Delivery Address is the same as Home Address?',
    },
    applicant_delivery_address_street: {
        ...schemas.textRequired,
        section: 'Delivery Address',
        label: 'Delivery Address',
        validation: Joi.when('applicant_delivery_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'address-line1',
    },
    applicant_delivery_address_city: {
        ...schemas.textRequired,
        section: 'Delivery Address',
        label: 'Delivery Address City',
        validation: Joi.when('applicant_delivery_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'address-level2',
    },
    applicant_delivery_address_state: {
        ...schemas.states,
        section: 'Delivery Address',
        label: 'Delivery Address State',
        validation: Joi.when('applicant_delivery_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'address-level1',
    },
    applicant_delivery_address_zip: {
        ...schemas.textRequired,
        section: 'Delivery Address',
        label: 'Delivery Address Zip',
        validation: Joi.when('applicant_delivery_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'postal-code',
    },
    applicant_phone_primary: {
        ...schemas.phoneRequired,
        section: 'Phone & Email',
        label: 'Primary Phone',
        autocomplete: 'tel',
    },
    applicant_email: {
        ...schemas.emailRequired,
        section: 'Phone & Email',
        label: 'E-mail address',
        autocomplete: 'email',
    },
    applicant_phone_alternate: {
        ...schemas.phoneOptional,
        section: 'Phone & Email',
        label: 'Alternate Phone',
    },
    applicant_phone_sat: {
        ...schemas.satPhone,
        section: 'Phone & Email',
        label: 'Sat Phone',
        validation: schemas.satPhone.validation.allow('', null),
    },
    applicant_phone_work: {
        ...schemas.phoneOptional,
        section: 'Phone & Email',
        label: 'Work Phone',
    },
    applicant_prefered_contact_method: {
        ...schemas.selectRequired,
        section: 'Preferred Contact Method',
        label: 'What is your preferred contact method for the USCG NMC to reach you?',
        options: {
            'Home Address': 'Home Address',
            'Delivery Address': 'Delviery Address',
            'Primary Phone Number': 'Primary Phone Number',
            'Altenate Phone Number': 'Alternate Phone Number',
            'Email Address': 'Email Address',
        },
    },
    applicant_emergency_contact_address_same_as_home: {
        ...schemas.boolRequired,
        section: 'Emergency Contact',
        label: 'Is your Emergency Contact Address the same as Home Address?',
    },
    applicant_emergency_contact_name_first: {
        ...schemas.textRequired,
        section: 'Emergency Contact',
        label: 'EC First Name',
        autocomplete: 'emergency given-name',
    },
    applicant_emergency_contact_name_last: {
        ...schemas.textRequired,
        section: 'Emergency Contact',
        label: 'EC Last Name',
        autocomplete: 'emergency family-name',
    },
    applicant_emergency_contact_address_street: {
        ...schemas.textRequired,
        section: 'Emergency Contact',
        label: 'EC Address',
        validation: Joi.when('applicant_emergency_contact_address_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'emergency address-line1',
    },
    applicant_emergency_contact_address_city: {
        ...schemas.textRequired,
        section: 'Emergency Contact',
        label: 'EC Address City',
        validation: Joi.when('applicant_emergency_contact_address_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'emergency address-level2',
    },
    applicant_emergency_contact_address_state: {
        ...schemas.states,
        section: 'Emergency Contact',
        label: 'EC Address State',
        validation: Joi.when('applicant_emergency_contact_address_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autocomplete: 'emergency address-level1',
    },
    applicant_emergency_contact_address_zip: {
        ...schemas.textRequired,
        section: 'Emergency Contact',
        label: 'EC Address Zip',
        validation: Joi.when('applicant_emergency_contact_address_same_as_home', {
            is: false,
            then: schemas.textRequired.validation,
            otherwise: schemas.textOptional.validation,
        }),
        autcomplete: 'emergency postal-code',
    },
    applicant_emergency_contact_relationship: {
        ...schemas.textOptional,
        section: 'Emergency Contact',
        label: 'EC Relationship (Optional)',
    },
    applicant_emergency_contact_phone_primary: {
        ...schemas.phoneOptional,
        section: 'Emergency Contact',
        label: 'EC Primary Phone (Optional)',
        autocomplete: 'emergency tel',
    },
    applicant_emergency_contact_phone_alternate: {
        ...schemas.phoneOptional,
        section: 'Emergency Contact',
        label: 'EC Alternate Phone (Optional)',
    },
    applicant_emergency_contact_email: {
        ...schemas.emailOptional,
        section: 'Emergency Contact',
        label: 'EC Email Address (Optional)',
        autocomplete: 'emergency email',
    },
    addicted_to_drugs_in_last_ten_years: {
        ...schemas.boolRequired,
        section: 'Past Convictions & Drug Tests',
        label: 'Have you ever been a user of/or addicted to a dangerous drug, including marijuana, within the last 10 years?',
    },
    convicted_of_drug_violation: {
        ...schemas.boolRequired,
        section: 'Past Convictions & Drug Tests',
        label: 'Have you ever been convicted of violating a dangerous drug law of the United States, District of Columbia, or any state, or territory of the United States?',
    },
    convicted_in_any_court: {
        ...schemas.boolRequired,
        section: 'Past Convictions & Drug Tests',
        label: 'Have you ever been convicted by any court-including military court - for an offence other than a minor traffic violation?',
    },
    convicted_traffic_infractions: {
        ...schemas.boolRequired,
        section: 'Past Convictions & Drug Tests',
        label: 'Have you ever been convicted of a traffic infraction arising in a connection with a fatal traffic accident, reckless driving or racing on a highway or operating a motor vehicle while under the influence of, or impaired by, alcohol or a controlled substance?',
    },
    drivers_license_revoked_DUI: {
        ...schemas.boolRequired,
        section: 'Past Convictions & Drug Tests',
        label: 'Have you ever had your drivers license revoked or suspended for refusing to an alcohol or drug test?',
    },
    failed_a_drug_test_last_ten_years: {
        ...schemas.boolRequired,
        section: 'Past Convictions & Drug Tests',
        label: 'Have you had a drug test with a result other than negative within the last 10-years?',
    },
    ndr_to_uscg_authorization: {
        ...schemas.boolRequired,
        section: 'National Driver Registry Consent',
        label: "I authorize the National Driver Registry to furnish the U.S. Coast Guard (USCG) information pertaining to my driving record. This consent constitutes authorization for a single access to the information contained in the NDR to verify information provided in this application. I understand the USCG will make the information received from the NDR available to me for review and written comment prior to disapproving my application of taking action against my Merchant Mariner's Credential. Authority: 46 U.S.C. 710(g), 46 U.S.C. 7302(c), and 46 U.S.C. 7505.",
    },
};

export default fields;
